/* eslint-disable max-len */
import { useState } from "react";
import { useTranslate } from "react-admin";
import { muiColors } from "themes/colors";
import {
  Avatar,
  Dialog,
  DialogTitle,
  IconButton,
  Snackbar,
  Tooltip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

interface LimitedCustomerPreviewModalProps {
  user: any;
  onClose: () => void;
}

export default function LimitedCustomerPreviewModal({
  user,
  onClose
}: LimitedCustomerPreviewModalProps) {
  const translate = useTranslate();
  return (
    <Dialog
      open={Boolean(user?.id)}
      onClose={onClose}
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: muiColors.paper,
          borderRadius: "8px",
          margin: "12px"
        },
        "& #edit-dialog-title": {
          textAlign: "center",
          textTransform: "uppercase",
          fontFamily: "Inter",
          fontSize: "14px"
        },
        "& .RaFormDialogTitle-closeButton": {
          display: "none"
        }
      }}
    >
      <DialogTitle>
        <div className="text-[20px] px-6 text-center">
          {translate("appointment.client_details")}
        </div>
        <IconButton
          sx={{
            position: "absolute",
            top: "8px",
            right: "8px"
          }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <div>
        <div className="px-4 pb-4 bt-0 w-[350px]">
          <div className="text-[12px] mb-1 font-[400] font-['Inter'] text-mono-secondaryTextColor">
            {translate("appointment.client")}:
          </div>
          <div className={`flex just items-center gap-[8px] mt-[2px]`}>
            <Avatar
              sx={{
                bgcolor: "rgba(238, 234, 228, 0.9)",
                color: "rgba(48, 47, 46, 0.95)",
                width: "32px",
                height: "32px",
                fontFamily: "Inter",
                fontSize: "18px"
              }}
              alt={`${user?.first_name} ${user?.last_name}`}
              src={user?.photo_url || "Monogram"}
            />
            <div className="line-clamp-1 break-all text-[16px] font-[400] font-['Inter'] text-mono-primaryTextColor">
              {`${user?.first_name} ${user?.last_name}`}
            </div>
          </div>
          {user?.email ? (
            <>
              <div className="text-[12px] mt-3 font-[400] font-['Inter'] text-mono-secondaryTextColor">
                Email:
              </div>
              <div className={`flex just items-center gap-[8px] mt-[2px]`}>
                <div className="line-clamp-2 break-all text-[16px] font-[400] font-['Inter'] text-mono-primaryTextColor">
                  {user?.email}
                </div>
                <CopyToClipboardButton textToCopy={user?.email} />
              </div>
            </>
          ) : (
            <>
              <div className="text-[12px] mt-3 font-[400] font-['Inter'] text-mono-secondaryTextColor">
                {translate("appointment.client_phone_number")}:
              </div>
              <div className={`flex just items-center gap-[8px] mt-[2px]`}>
                <div className="line-clamp-2 break-all text-[16px] font-[400] font-['Inter'] text-mono-primaryTextColor">
                  {user?.phone_number || "-"}
                </div>
                <CopyToClipboardButton textToCopy={user?.libphonenumber} />
              </div>
            </>
          )}
        </div>

        <IconButton
          sx={{
            position: "absolute",
            top: "8px",
            right: "8px"
          }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </div>
    </Dialog>
  );
}

const CopyToClipboardButton = ({
  textToCopy
}: {
  textToCopy: string | undefined;
}) => {
  const [open, setOpen] = useState(false);

  const handleClick = () => {
    setOpen(true);
    navigator?.clipboard?.writeText(textToCopy || "");
  };

  return (
    <>
      <Tooltip title={open ? "Copied" : "Copy"} placement="top">
        <IconButton
          sx={{ width: 36, height: 36 }}
          onClick={handleClick}
          color="primary"
        >
          <ContentCopyIcon sx={{ width: 24, height: 24 }} />
        </IconButton>
      </Tooltip>
      <Snackbar
        message={`Copied "${textToCopy}"`}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={2000}
        onClose={() => setOpen(false)}
        open={open}
      />
    </>
  );
};
